import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { CruisesContract } from '@luxuryescapes/contract-svc-cruise';
import { Order } from '@luxuryescapes/contract-svc-order';

import GridPagination from '~/components/Common/Elements/GridPagination';
import { MultiFilterState } from '~/components/Common/Forms/OrderSearchForm';

import { ITEM_TYPE_CRUISE } from '~/consts/order';
import { ROLE_ICS_STORE_TEAM } from '~/consts/roles';

import useCurrentUser from '~/hooks/useCurrentUser';

import { getPurchases } from '~/services/OrdersService';
import vendorService from '~/services/VendorsService';
import cruiseService, { BookingList, BookingListResponse } from '~/services/cruises/BookingInfoService';

import {
  customerDataFormatter,
  orderStatusFormatter,
  orderTotalFormatter,
  packageInformationFormatter,
} from '../formatters';
import cruiseOfferDetailsFormatter from '../formatters/cruiseOfferDetailsFormatter';
import orderLinkFormatter from '../formatters/orderLinkFormatter';

type OrderResponse = {
  total: number;
  result: Array<Order.Order>;
};

type VendorResponse = {
  id: string;
  name: string;
};

const DEFAULT_ROWS_RESPONSE: Rows = { result: [], total: 0 };

type FetchOrderDataParams = {
  filterField: string;
  filterValue: string;
  additionalSearchParams: MultiFilterState;
  brand: App.Brands;
  customerId: string;
  sizePerPage: number;
  currentPage: number;
};

interface Props {
  page: number;
  additionalSearchParams: MultiFilterState;
  filterField: string;
  filterValue: string;
  tenant: App.Tenant;
  customerId: string;
  sizePerPage: number;
  currentPage: number;
  onPageChange: (page: number) => void;
}

const columns: Array<GridColDef> = [
  {
    field: 'fk_customer_id',
    headerName: 'Customer',
    sortable: false,
    flex: 3,
    renderCell: customerDataFormatter,
    display: 'flex',
  },
  {
    flex: 2,
    sortable: false,
    field: 'offer_details',
    headerName: 'Vendor/Ship Name',
    renderCell: cruiseOfferDetailsFormatter,
    display: 'flex',
  },
  {
    field: 'status',
    headerName: 'Order Status',
    flex: 2,
    sortable: false,
    renderCell: orderStatusFormatter,
    display: 'flex',
  },
  {
    field: 'vendor_booking_details',
    headerName: 'Vendor Booking',
    sortable: false,
    flex: 2,
    renderCell: packageInformationFormatter,
    display: 'flex',
  },
  { field: 'currency_code', headerName: 'Currency', flex: 1, sortable: false, display: 'flex' },
  {
    field: 'total',
    headerName: 'Order Total',
    sortable: false,
    flex: 1,
    renderCell: orderTotalFormatter,
    display: 'flex',
  },
  { field: 'utm_source', headerName: 'Source', flex: 1, sortable: false, display: 'flex' },
  { field: 'id', headerName: 'View Order', sortable: false, flex: 1, renderCell: orderLinkFormatter, display: 'flex' },
];

type Rows = {
  total: number;
  result: Array<RowDetails>;
};

type RowDetails = Array<Order.Order & CruisesContract.BookingBatchDetailsResponse>;
type OrderWithProductType = Order.Order & {
  tour_items: Array<Order.TourItem & { product_type: string | null }>;
};

const buildRowsContent = (
  orders: Array<OrderWithProductType>,
  cruisesDetail: BookingList,
  vendors: Array<VendorResponse>,
): Array<RowDetails> => {
  const rows = [];
  orders.forEach((order: OrderWithProductType) => {
    if (
      order.tour_items.length > 0 &&
      order.tour_items.some(
        (tourItem: OrderWithProductType['tour_items'][0]) => tourItem.product_type === 'cruise_tour',
      )
    ) {
      const vendor = vendors.find((vendor) => vendor.id === order.tour_items[0].vendor_id);
      rows.push({ ...order, vendorName: vendor.name });
      return;
    }

    const cruise = cruisesDetail.find((cruise) => order.cruise_items.some((item) => item.booking_id === cruise.id));

    rows.push({ ...order, ...cruise });
  });
  return rows;
};

export default function CruisesPurchaseList({
  page,
  tenant,
  filterField,
  filterValue,
  additionalSearchParams,
  customerId,
  currentPage,
  sizePerPage,
  onPageChange,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useCurrentUser();

  const [rows, setRows] = useState<Rows>(DEFAULT_ROWS_RESPONSE);
  const [fetching, setFetching] = useState<Utils.FetchingState>('idle');

  const fetchData = useCallback(
    async (params: FetchOrderDataParams): Promise<OrderResponse> => {
      setFetching('loading');
      if (user?.roles.includes(ROLE_ICS_STORE_TEAM)) {
        // we don't want these users to be able to see all results so
        // if there are no filters selected, just return empty
        if (!params.filterField || !params.filterValue) {
          setFetching('success');
          return { total: 0, result: [] };
        }
      }

      try {
        const fetchedPurchases = await getPurchases({
          page: params.currentPage,
          brand: params.brand,
          per_page: params.sizePerPage,
          customer_id: params.customerId,
          item_type: ITEM_TYPE_CRUISE,
          filterBy: params.filterField,
          filterValue: params.filterValue,
          additionalFilter: params.additionalSearchParams,
        });

        setFetching('success');

        if (!fetchedPurchases || fetchedPurchases.result.length === 0) {
          enqueueSnackbar('No results found', { autoHideDuration: 5000, variant: 'warning' });
        }

        return {
          total: fetchedPurchases.total,
          result: fetchedPurchases.result,
        };
      } catch (error) {
        setFetching('failed');
        enqueueSnackbar(JSON.stringify(error), { autoHideDuration: 5000, variant: 'error' });

        return { total: 0, result: [] };
      }
    },
    [enqueueSnackbar, user?.roles],
  );

  const fetchCruiseDetails = useCallback(
    async (orders: OrderResponse): Promise<BookingListResponse> => {
      const bookingIds =
        orders.result.flatMap((order: Order.Order) => order.cruise_items.map((item) => item.booking_id)) ?? [];

      try {
        const response = await cruiseService.getBatchBookingsById(bookingIds);

        return {
          result: response.result,
          status: response.status,
        };
      } catch (error) {
        setFetching('failed');
        enqueueSnackbar(JSON.stringify(error), { autoHideDuration: 5000, variant: 'error' });

        return { result: [], status: 400 };
      }
    },
    [enqueueSnackbar],
  );

  const fetchDataAndBuildContent = useCallback(
    async (params: FetchOrderDataParams) => {
      const orderResponse = await fetchData(params);
      const toursyCruiseUniqueVendorIds = Array.from(
        new Set(
          orderResponse.result
            .flatMap((order) => order.tour_items)
            .map((tourItem) => tourItem.vendor_id)
            .filter(Boolean),
        ),
      );

      const vendorResponse = await vendorService.getVendorsByIds(
        toursyCruiseUniqueVendorIds.join(','),
        toursyCruiseUniqueVendorIds.length,
      );
      const cruiseResponse = await fetchCruiseDetails(orderResponse);
      const content = buildRowsContent(
        orderResponse.result as OrderWithProductType[],
        cruiseResponse.result,
        vendorResponse.result,
      );

      setRows({ result: content, total: orderResponse.total });
    },
    [fetchCruiseDetails, fetchData],
  );

  useEffect(() => {
    if (user) {
      fetchDataAndBuildContent({
        customerId,
        filterField,
        filterValue,
        additionalSearchParams,
        sizePerPage,
        currentPage,
        brand: tenant.brand,
      });
    }
  }, [
    currentPage,
    customerId,
    filterField,
    filterValue,
    additionalSearchParams,
    sizePerPage,
    tenant.brand,
    fetchDataAndBuildContent,
    user,
  ]);

  return (
    <DataGrid
      pagination
      autoHeight
      rows={rows.result}
      rowCount={rows.total}
      columns={columns}
      disableColumnMenu
      disableColumnFilter
      paginationMode="server"
      disableRowSelectionOnClick
      getRowHeight={() => 'auto'}
      pageSizeOptions={[sizePerPage]}
      loading={fetching === 'loading'}
      slots={{ pagination: GridPagination }}
      paginationModel={{ page: page - 1, pageSize: sizePerPage }}
      onPaginationModelChange={({ page }) => onPageChange(page + 1)}
    />
  );
}
